import { Badge, Input } from '@sea-snag/ui';
import { X } from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { z } from 'zod';

const emailSchema = z.string().email();

interface EmailTagInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  maxEmails?: number;
}

export function EmailTagInput({
  value,
  onChange,
  placeholder = 'colleague@example.com',
  maxEmails = 20,
}: EmailTagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addEmail = (raw: string) => {
    const email = raw.trim().toLowerCase();
    if (!email) return;

    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError('Invalid email address');
      return;
    }

    if (value.includes(email)) {
      setError('Email already added');
      return;
    }

    if (value.length >= maxEmails) {
      setError(`Maximum ${maxEmails} emails`);
      return;
    }

    setError(null);
    onChange([...value, email]);
    setInputValue('');
  };

  const removeEmail = (email: string) => {
    onChange(value.filter((e) => e !== email));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab' || e.key === ' ') {
      e.preventDefault();
      addEmail(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeEmail(value[value.length - 1]!);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    // Split pasted text by commas, semicolons, spaces, or newlines
    const rawEmails = text.split(/[,;\s\n\r]+/);
    if (rawEmails.length > 1) {
      e.preventDefault();
      let nextEmails = [...value];
      let nextError: string | null = error;

      for (const raw of rawEmails) {
        const email = raw.trim().toLowerCase();
        if (!email) continue;

        const result = emailSchema.safeParse(email);
        if (!result.success) {
          nextError = 'Invalid email address';
          continue;
        }

        if (nextEmails.includes(email)) {
          nextError = 'Email already added';
          continue;
        }

        if (nextEmails.length >= maxEmails) {
          nextError = `Maximum ${maxEmails} emails`;
          continue;
        }

        nextError = null;
        nextEmails = [...nextEmails, email];
      }

      if (nextEmails.length > value.length) {
        onChange(nextEmails);
        setInputValue('');
      }
      setError(nextError);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex min-h-[42px] flex-wrap gap-1.5 rounded-md border border-input bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1">
        {value.map((email) => (
          <Badge key={email} variant="secondary" className="animate-reveal-scale gap-1 pr-1">
            {email}
            <button
              type="button"
              onClick={() => removeEmail(email)}
              className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
              aria-label={`Remove ${email}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          type="email"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            if (inputValue) addEmail(inputValue);
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          className="min-w-[180px] flex-1 border-0 p-0 shadow-none focus-visible:ring-0"
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <p className="text-xs text-muted-foreground">
        Press Enter, Space, or comma to add. Paste multiple emails at once.
      </p>
    </div>
  );
}
